import { findApplication, normalizeOrigin, type ApplicationRegistry } from '@traceio-org/platform-contracts';
import type { FixtureConfig } from './config';

export type FixtureRegistration = { ok: true; audience: string } | { ok: false; errors: string[] };

function sameOrigin(a: string, b: string): boolean {
  try {
    return normalizeOrigin(a) === normalizeOrigin(b);
  } catch {
    return false;
  }
}

/**
 * The second guard of the fixture (after the build environment): it runs only where platform-api's
 * registry, for this very environment, marks this page's origin as the navigation fixture of this product.
 * Where the real product is registered, where the product is not deployed, or on any other origin (the
 * agents server must never pose as carriers), it refuses to start. The audience it returns is the
 * product's registry audience, so tokens are requested for exactly what platform-api would mint.
 */
export function checkFixtureRegistration(
  registry: ApplicationRegistry,
  config: FixtureConfig,
  pageOrigin: string,
): FixtureRegistration {
  const errors: string[] = [];
  if (registry.environment !== config.environment) {
    errors.push(
      `The registry is for ${registry.environment} but this fixture is built for ${config.environment}.`,
    );
  }
  if (!sameOrigin(registry.hub.origin, config.hubOrigin)) {
    errors.push(
      `VITE_HUB_ORIGIN is ${config.hubOrigin} but the registry's account hub is ${registry.hub.origin}.`,
    );
  }
  const application = findApplication(registry, config.productKey);
  if (!application) {
    errors.push(`The registry has no ${config.productKey} application.`);
    return { ok: false, errors };
  }
  const destination = application.destination;
  if (application.status === 'unavailable' || !destination) {
    errors.push(`${application.displayName} is not deployed in this environment.`);
  } else {
    if (destination.fixture !== true) {
      errors.push(
        `The registry does not mark ${application.displayName} at ${destination.origin} as a navigation fixture: it is not a navigation fixture here.`,
      );
    }
    if (!sameOrigin(destination.origin, pageOrigin)) {
      errors.push(
        `This page runs at ${pageOrigin}, but the ${config.productKey} fixture is registered at ${destination.origin}.`,
      );
    }
  }
  if (!application.audience) errors.push(`The registry gives ${application.displayName} no audience.`);
  if (errors.length > 0 || !application.audience) return { ok: false, errors };
  return { ok: true, audience: application.audience };
}
